import React, { useEffect, useMemo, useState } from "react";
import "./../../../styles/SupportDashboard.css";
import Button from './../../ui/Button';

export default function ActionTransfersModal({
  isOpen,
  onClose,
  sites = [],
  selectedGroupId,          
  onGroupChange,            
  transfers = [],
  loading = false,
  onRefresh,                
  onSubmit,                 
}) {
  const [query, setQuery] = useState("");
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const [newGroupQuery, setNewGroupQuery] = useState("");
  const [newGroupId, setNewGroupId] = useState("");

  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [err, setErr] = useState({});

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setSelectedTransfer(null);
    setNewGroupQuery("");
    setNewGroupId("");
    setSaving(false);
    setSuccess("");
    setErr({});
  }, [isOpen]);

  const siteOptions = useMemo(() => {
    const normalized = (sites || [])
      .map((s) => ({
        label: s.sitename,
        value: String(s.id),
        level: s.level || "",
      }))
      .filter((s) => s.value !== "1");

    const q = newGroupQuery.trim().toLowerCase();
    const filtered = !q
      ? normalized
      : normalized.filter((x) =>
          `${x.label} ${x.level} ${x.value}`.toLowerCase().includes(q)
        );

    filtered.sort((a, b) => (a.level?.length || 0) - (b.level?.length || 0));
    return filtered.slice(0, q ? 50 : 200);
  }, [sites, newGroupQuery]);

const filteredTransfers = useMemo(() => {
  const q = query.trim().toLowerCase();
  if (!q) return transfers;

  return transfers.filter((t) => {
    const text =
      `${t.groupName ?? ""} ${t.groupId ?? ""} ${t.visorId ?? ""} ${t.requester ?? ""} ${t.description ?? ""} ${t.requestDate ?? ""}`
        .toLowerCase();
    return text.includes(q);
  });
}, [transfers, query]);


  const validate = () => {
    const e = {};
    if (!selectedTransfer) e.transfer = "Select a transfer first.";
    if (!newGroupId) e.newGroupId = "Select the destination group.";
    setErr(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;

    setSaving(true);
    setErr({});
    setSuccess("");

    try {
      const visorRef = selectedTransfer?.visorId;

      await onSubmit({
  visorRef: String(visorRef),
  newGroupId: Number(newGroupId),
      });

      setSuccess(`Transfer actioned for ${visorRef}.`);
      setTimeout(() => onClose(), 1000);
    } catch (ex) {
      const api = ex?.body ?? ex?.response?.data ?? null;
      const errors = Array.isArray(api?.errors) ? api.errors : [];
      const messages = errors.map((e) => e?.message).filter(Boolean);

      setErr((prev) => ({
        ...prev,
        api: messages.length ? messages.join(" • ") : (api?.message || ex?.message || "Failed to action transfer."),
      }));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="sd-modal-backdrop" role="dialog" aria-modal="true">
      <div className="sd-modal sd-modal-lg">
        <div className="sd-modal-header">
          <div>
            <h2 className="sd-title">Action transfers</h2>
            <div className="sd-subtitle">View transfer requests and action them into a new group.</div>
          </div>
          <button className="sd-icon-btn" onClick={onClose} aria-label="Close" type="button">
            ✕
          </button>
        </div>

        <div className="sd-modal-body">
          {err.api && <div className="sd-alert sd-alert-bad">{err.api}</div>}
          {success && <div className="sd-alert sd-alert-good">{success}</div>}
          {err.transfer && <div className="sd-alert sd-alert-bad">{err.transfer}</div>}

          <div className="sd-field">
            <div className="sd-label">Transfer context (selected site/group)</div>
            <div className="sd-inline">
              <select
                className="sd-input"
                value={String(selectedGroupId ?? "")}
                onChange={(e) => onGroupChange?.(Number(e.target.value))}
              >
                <option value="1">All Groups</option>
                {sites
                  .filter((s) => s.id !== 1)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.sitename} ({s.id})
                    </option>
                  ))}
              </select>

<Button onClick={onRefresh} buttonStyle="btn-main">
                  {loading ? "Refreshing…" : "Refresh"}
                </Button>
              {/* <button className="sd-secondary-btn" type="button" onClick={onRefresh} disabled={loading}>
                {loading ? "Refreshing…" : "Refresh"}
              </button> */}
            </div>
          </div>

          <div className="sd-inline">
            <input
              className="sd-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search transfers by group / visor / requester / description…"
              autoComplete="off"
            />
            {query && (
              <button className="sd-secondary-btn" type="button" onClick={() => setQuery("")}>
                Clear
              </button>
            )}
          </div>

          <div className="sd-transfer-table">
            <div className="sd-transfer-head">
              <div>Group</div>
              <div>Visor Reference</div>
              <div>Requester</div>
              <div>Description</div>
              <div>Request Date</div>
              <div>Transfer actions</div>
            </div>

            {loading ? (
              <div className="sd-transfer-empty">Loading transfers…</div>
            ) : filteredTransfers.length === 0 ? (
              <div className="sd-transfer-empty">No transfers found.</div>
            ) : (
filteredTransfers.map((t) => {
  const isSel = String(selectedTransfer?.id) === String(t.id);

  return (
    <button
      key={t.id}
      type="button"
      className={`sd-transfer-row ${isSel ? "is-selected" : ""}`}
      onClick={() => setSelectedTransfer(t)}
    >
      <div className="sd-cell">{t.groupName || "-"}</div>
      <div className="sd-cell sd-strong">{t.visorId || "-"}</div>
      <div className="sd-cell">{t.requester || "-"}</div>
      <div className="sd-cell sd-wrap">{t.description || "-"}</div>
      <div className="sd-cell">
        {t.requestDate ? new Date(t.requestDate).toLocaleString("en-GB") : "-"}
      </div>
      <div className="sd-cell">
        <span className={`sd-pill ${isSel ? "is-on" : ""}`}>{isSel ? "Selected" : "Select"}</span>
      </div>
    </button>
  );
})
            )}
          </div>

          <div className="sd-divider" />
          <div className="sd-field">
            <div className="sd-label">Destination group</div>
            <input
              className="sd-input"
              value={newGroupQuery}
              onChange={(e) => setNewGroupQuery(e.target.value)}
              placeholder="Search destination group…"
              autoComplete="off"
            />

            <div className="sd-dropdown">
              {siteOptions.length === 0 ? (
                <div className="sd-dropdown-empty">No matches</div>
              ) : (
                siteOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    className={`sd-dd-item ${String(newGroupId) === String(s.value) ? "is-selected" : ""}`}
                    onClick={() => {
                      setNewGroupId(s.value);
                      setNewGroupQuery(s.label);
                    }}
                  >
                    <span className="sd-dd-label">{s.label}</span>
                    <span className="sd-dd-meta">{s.level ? s.level : `#${s.value}`}</span>
                  </button>
                ))
              )}
            </div>

            {err.newGroupId && <div className="sd-error">{err.newGroupId}</div>}
          </div>
        </div>

        <div className="sd-modal-footer">
          {/* <button className="sd-secondary-btn" onClick={onClose} disabled={saving} type="button">
            Close
          </button> */}
          <Button onClick={onClose} buttonStyle="btn-alternate" disabled={saving}>
            Close
          </Button>
          <Button
            onClick={submit}
            buttonStyle="btn-main"
            disabled={saving || !selectedTransfer || !newGroupId}
          >
            {saving ? "Submitting…" : "Action transfer"}
          </Button>
          
          {/* <button
            className="sd-primary-btn"
            onClick={submit}
            disabled={saving || !selectedTransfer || !newGroupId}
            type="button"
          >
            {saving ? "Submitting…" : "Action transfer"}
          </button> */}
        </div>
      </div>
    </div>
  );
}
